import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterSearchInput from 'components/Common/FilterPanel/FilterSearchInput'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { max } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { pluralizeToString } from 'lib/string/pluralize'
import TextButton from 'LuxKit/Button/TextButton'
import RadioInput from 'LuxKit/Radio/RadioInput'
import DualSlider from 'LuxKit/Slider/DualSlider'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLabel from 'luxPlus/components/LuxPlusLabel'
import { rem } from 'polished'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { useAppSelector } from '../../../hooks/reduxHooks'

const InputGroup = styled(Group)`
  margin-top: ${rem(16)};
`

const ActionTextButton = styled(TextButton)`
  padding: 0;
`

const FilterHeader = styled(Group)`
  padding-top: ${rem(8)};
  padding-bottom: ${rem(2)};
`

function toggleSelectedSet(selectedSet: Set<string>, value: string): Set<string> {
  const nextSelectedSet = new Set([...selectedSet.values()])
  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }
  return nextSelectedSet
}

export function generateOptionItems(
  options: Array<App.Tours.TourDealFacetOption>,
  selectedOptions: Array<string> = [],
  filterString = '',
) {
  return options.filter((option) =>
    option.value.toLowerCase().includes(filterString.toLowerCase()) || selectedOptions.includes(option.value),
  ).sort((a, b) => {
    if (selectedOptions.includes(a.value) && !selectedOptions.includes(b.value)) return -1
    if (!selectedOptions.includes(a.value) && selectedOptions.includes(b.value)) return 1
    if (a.count > b.count) return -1
    if (a.count < b.count) return 1
    return 0
  }).map(({ value, count }) => ({
    label: (
      <Group direction="horizontal" horizontalAlign="space-between">
        {value}
        <span>({count})</span>
      </Group>
    ),
    value,
    checked: selectedOptions.includes(value),
  }))
}

export interface FilterState {
  sortBy: string
  tourStyles: Set<string>
  operators: Set<string>
  priceMin: string
  priceMax: string
  durationMin: string
  durationMax: string
  countries: Set<string>
  categories: Set<string>
  luxPlusFeatures: Set<string>
}
type FilterKey = keyof FilterState

function isSetFilter(value: unknown): value is Set<string> {
  return value instanceof Set
}

const convertToNumber = (value: string | number | undefined): number => {
  if (value === undefined) return 0
  if (typeof value === 'string') return parseInt(value)
  return value
}

interface FilterChangeEvent {
  target: {
    value: string
  }
}

interface Props {
  modalView?: boolean
  searchFacets?: App.Tours.TourDealFacets
  initialFacets: App.Tours.TourDealFacets
  onFilterChange: (values: App.Tours.TourDealFiltersApplied) => void
  filtersApplied: App.Tours.TourDealFiltersApplied
  onSortChange?: (key: string) => void
  sortOrderApplied?: string
}

function transformStateToEvent(filters: FilterState): App.Tours.TourDealFiltersApplied {
  return ({
    countries: filters.countries !== undefined && filters.countries.size > 0 ? Array.from(filters.countries) : undefined,
    operators: filters.operators !== undefined && filters.operators.size > 0 ? Array.from(filters.operators) : undefined,
    categories: filters.categories !== undefined && filters.categories.size > 0 ? Array.from(filters.categories) : undefined,
    luxPlus: filters.luxPlusFeatures !== undefined && filters.luxPlusFeatures.size > 0 ? Array.from(filters.luxPlusFeatures) : undefined,
    tourStyles: filters.tourStyles !== undefined && filters.tourStyles.size > 0 ? Array.from(filters.tourStyles) : undefined,
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    durationMin: filters.durationMin,
    durationMax: filters.durationMax,
  })
}

interface SortOption {
  label: string
  value: 'price.asc' | 'price.desc' | 'duration.asc' | 'duration.desc' | 'countries.asc' | 'countries.desc'
}

const sortOptions: Array<SortOption> = [
  { label: 'Price (Lowest)', value: 'price.asc' },
  { label: 'Price (Highest)', value: 'price.desc' },
  { label: 'Duration (Shortest)', value: 'duration.asc' },
  { label: 'Duration (Longest)', value: 'duration.desc' },
  { label: 'Number of countries (Lowest)', value: 'countries.asc' },
  { label: 'Number of countries (Highest)', value: 'countries.desc' },
]

function TourDealFilterContent(props: Props) {
  const {
    modalView = false,
    searchFacets,
    initialFacets,
    onFilterChange = noop,
    filtersApplied,
    onSortChange = noop,
    sortOrderApplied,
  } = props

  const luxPlusTourEnabled = useAppSelector(getIsLuxPlusToursEnabled)

  const [countriesSearch, setCountriesSearch] = useState<string>('')

  const initialState: FilterState = useMemo(
    () => {
      return ({
        tourStyles: new Set(filtersApplied?.tourStyles),
        operators: new Set(filtersApplied?.operators),
        priceMin: filtersApplied?.priceMin?.toString() ?? initialFacets?.price?.min.toString() ?? '0',
        priceMax: filtersApplied?.priceMax?.toString() ?? initialFacets?.price?.max.toString() ?? '0',
        durationMin: filtersApplied?.durationMin?.toString() ?? initialFacets?.duration?.min.toString() ?? '0',
        durationMax: filtersApplied?.durationMax?.toString() ?? initialFacets?.duration?.max.toString() ?? '0',
        countries: new Set(filtersApplied?.countries),
        sortBy: sortOrderApplied ?? 'price.asc',
        categories: new Set(filtersApplied?.categories),
        luxPlusFeatures: new Set(filtersApplied?.luxPlus),
      })
    },
    [filtersApplied, initialFacets, sortOrderApplied],
  )

  const [filters, setFilters] = useState(initialState)
  const onAllFiltersReset = useCallback(() => {
    const unselectedState: FilterState = {
      sortBy: initialState.sortBy,
      priceMin: initialFacets.price.min.toString(),
      priceMax: initialFacets.price.max.toString(),
      durationMin: initialFacets.duration.min.toString(),
      durationMax: initialFacets.duration.max.toString(),
      luxPlusFeatures: new Set(),
      operators: new Set(),
      tourStyles: new Set(),
      categories: new Set(),
      countries: new Set()
    }
    setFilters(unselectedState)
    onFilterChange(transformStateToEvent(unselectedState))
  }, [initialState.sortBy, initialFacets, onFilterChange])

  const onFilterChangeCallback = useCallback(
    (key: FilterKey) => (e: FilterChangeEvent) => {
      const value = e.target.value
      const currentFilterValue = filters[key]
      const nextValue = isSetFilter(currentFilterValue) ?
          toggleSelectedSet(currentFilterValue, value) :
          new Set([value])
      const nextState = { ...filters, [key]: nextValue }
      setFilters(nextState)
      onFilterChange?.(transformStateToEvent(nextState))
    },
    [filters, onFilterChange],
  )

  const onFilterResetCallback = useCallback(
    (key: FilterKey) => () => {
      const nextState = { ...filters, [key]: new Set() }
      setFilters(nextState)
      onFilterChange(transformStateToEvent(nextState))
    },
    [filters, onFilterChange],
  )

  const onSortChangeCallback = useCallback(
    (key: string) => () => {
      const nextState = { ...filters, sortBy: key }
      setFilters(nextState)
      onSortChange(key)
    },
    [filters, onSortChange],
  )

  const [countries, operators, categories, styles, luxPlusFeatures] = useMemo(
    () => [
      searchFacets?.countries ?? [],
      searchFacets?.operators ?? [],
      searchFacets?.categories ?? [],
      searchFacets?.tourStyles ?? [],
      searchFacets?.luxPlusFeatures ?? [],
    ],
    [searchFacets],
  )

  const onCountriesChange = useCallback((e: FilterChangeEvent) => {
    setCountriesSearch('')
    onFilterChangeCallback('countries')(e)
  }, [onFilterChangeCallback])

  const onPriceRangeStartChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const nextState = { ...filters, priceMin: e.target.value }
      setFilters(nextState)
      onFilterChange(transformStateToEvent(nextState))
    }, [filters, onFilterChange],
  )

  const onPriceRangeEndChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const nextState = { ...filters, priceMax: e.target.value }
      setFilters(nextState)
      onFilterChange(transformStateToEvent(nextState))
    }, [filters, onFilterChange],
  )

  const priceSlider = useMemo(() => {
    const { max: priceMax } = initialFacets.price
    const step = priceMax <= 10000 ? 10 : 100
    return { step }
  }, [initialFacets.price])

  const durationSlider = useMemo(() => {
    const maxDuration = parseInt(initialState.durationMax)
    const minDuration = parseInt(initialState.durationMin)
    const step = maxDuration <= 100 ? 1 : 10
    const min = Math.floor(minDuration / step) * step
    const max = Math.ceil(maxDuration / step) * step
    return { min, max, step }
  }, [initialState])

  const onDurationMinChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const nextState = { ...filters, durationMin: e.target.value }
      setFilters(nextState)
      onFilterChange(transformStateToEvent(nextState))
    }, [filters, onFilterChange],
  )

  const onDurationMaxChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const nextState = { ...filters, durationMax: e.target.value }
      setFilters(nextState)
      onFilterChange(transformStateToEvent(nextState))
    }, [filters, onFilterChange],
  )

  const countriesItems = useMemo(
    () => generateOptionItems(countries, filtersApplied?.countries, countriesSearch),
    [countries, filtersApplied?.countries, countriesSearch],
  )

  const operatorsItems = useMemo(
    () => generateOptionItems(operators, filtersApplied?.operators),
    [operators, filtersApplied?.operators],
  )

  const categoriesItems = useMemo(
    () => generateOptionItems(categories, filtersApplied?.categories),
    [categories, filtersApplied?.categories],
  )

  const tourStyles = useMemo(
    () => generateOptionItems(styles, filtersApplied?.tourStyles),
    [styles, filtersApplied?.tourStyles],
  )

  const luxPlusFeatureItems = useMemo(
    () => generateOptionItems(luxPlusFeatures, filtersApplied?.luxPlus),
    [luxPlusFeatures, filtersApplied?.luxPlus],
  )

  const hasCategories = useMemo(
    () => categories.length > 0,
    [categories],
  )

  const hasLuxPlusFeatures = useMemo(
    () => luxPlusFeatures.length > 0,
    [luxPlusFeatures],
  )

  const noFilterAvailable =
    Math.max(countries.length, operators.length, tourStyles.length, categories.length) == 0 &&
    searchFacets?.price === undefined &&
    searchFacets?.duration === undefined

  const nbFacetOptionSelected = useMemo(() => {
    return (filtersApplied?.operators?.length ?? 0) +
      (filtersApplied?.countries?.length ?? 0) +
      (filtersApplied?.categories?.length ?? 0) +
      (filtersApplied?.luxPlus?.length ?? 0) +
      (filtersApplied?.tourStyles?.length ?? 0)
  }, [filtersApplied])

  const durationMin = useMemo(() => max([initialFacets?.duration?.min, durationSlider?.min, 0], convertToNumber) as number, [initialFacets?.duration?.min, durationSlider?.min])
  return (
    <>
      {!modalView && <FilterHeader direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Heading variant="heading4">Filter by</Heading>
        {nbFacetOptionSelected > 0 && <ActionTextButton kind="tertiary" onClick={onAllFiltersReset}>Reset all ({nbFacetOptionSelected})</ActionTextButton>}
      </FilterHeader>}

      {modalView && <CSSBreakpoint max="mobile">
        <FilterPanelFilterGroup title="Sort by">
          <InputGroup direction="vertical" gap={12}>
            {sortOptions.map((sortOption) => (
              <RadioInput
                key={sortOption.value}
                name={`tourDealSort-${modalView ? 'mobile' : 'desktop'}`}
                value={sortOption.value}
                onChange={onSortChangeCallback(sortOption.value)}
                checked={sortOption.value === filters.sortBy}
              >
                {sortOption.label}
              </RadioInput>
            ))}
          </InputGroup>
        </FilterPanelFilterGroup>
      </CSSBreakpoint>}
      {noFilterAvailable && (
        <BodyText variant="large">
          No filters are available for your search.
        </BodyText>
      )}
      { luxPlusTourEnabled && hasLuxPlusFeatures && <FilterPanelFilterGroup
        title={<LuxPlusLabel type="only-logo" />}
        canReset={filters.luxPlusFeatures?.size > 0}
        onReset={onFilterResetCallback('luxPlusFeatures')}
      >
        <FilterPanelCheckboxGroup
          name="luxPlusFeatures"
          items={luxPlusFeatureItems}
          onChange={onFilterChangeCallback('luxPlusFeatures')}
          maxItems={5}
        />
      </FilterPanelFilterGroup>}

      {!!searchFacets && priceSlider &&
        <FilterPanelFilterGroup title="Price">
          <DualSliderInput
            name="price"
            format="currency"
            min={initialFacets?.price?.min ?? 0}
            max={initialFacets?.price?.max ?? 0}
            startValue={Number(filters.priceMin)}
            endValue={Number(filters.priceMax)}
            step={priceSlider.step}
            onStartChange={onPriceRangeStartChange}
            onEndChange={onPriceRangeEndChange}
            variant="label"
          />
        </FilterPanelFilterGroup>}
      {!!searchFacets && !!durationSlider && !!filters.durationMax && !!filters.durationMin &&
        <FilterPanelFilterGroup title="Duration">
          <BodyText variant="medium">
            {pluralizeToString(
              'day',
              Number(durationMin),
            )}{' '}
            -{' '}
            {pluralizeToString(
              'day',
              Number(filters.durationMax),
            )}
          </BodyText>
          <DualSlider
            name="duration"
            min={initialFacets.duration.min ?? 0}
            max={initialFacets.duration.max ?? 0}
            defaultStartValue={durationMin}
            defaultEndValue={Number(filters.durationMax)}
            startValue={Number(filters.durationMin)}
            endValue={Number(filters.durationMax)}
            step={durationSlider.step}
            onStartChange={onDurationMinChange}
            onEndChange={onDurationMaxChange}
          />
        </FilterPanelFilterGroup>}
      {!!tourStyles.length && <FilterPanelFilterGroup
        title="Tour Style"
        canReset={filters.tourStyles.size > 0}
        onReset={onFilterResetCallback('tourStyles')}
      >
        <FilterPanelCheckboxGroup
          name="tourStyles"
          items={tourStyles}
          onChange={onFilterChangeCallback('tourStyles')}
        />
      </FilterPanelFilterGroup>}
      {hasCategories &&
        <FilterPanelFilterGroup
          title="Category"
          canReset={filters.categories.size > 0}
          onReset={onFilterResetCallback('categories')}
        >
          <FilterPanelCheckboxGroup
            name="categories"
            items={categoriesItems}
            onChange={onFilterChangeCallback('categories')}
            maxItems={10}
          />
        </FilterPanelFilterGroup>}
      {operators.length > 0 &&
        <FilterPanelFilterGroup
          title="Operator"
          canReset={filters.operators.size > 0}
          onReset={onFilterResetCallback('operators')}
        >
          <FilterPanelCheckboxGroup
            name="operators"
            items={operatorsItems}
            onChange={onFilterChangeCallback('operators')}
            maxItems={10}
          />
        </FilterPanelFilterGroup>}
      {countries.length > 0 &&
        <FilterPanelFilterGroup
          title="Countries"
          canReset={filters.countries?.size > 0}
          onReset={onFilterResetCallback('countries')}
        >
          <FilterSearchInput
            placeholder="Search for stops"
            onChange={setCountriesSearch}
            name="countriesSearch"
            value={countriesSearch}
          />
          <FilterPanelCheckboxGroup
            name="countries"
            items={countriesItems}
            onChange={onCountriesChange}
            maxItems={5}
          />
        </FilterPanelFilterGroup>}
    </>
  )
}

export default TourDealFilterContent
